import React, { FC, ReactElement, memo, useCallback, useState } from 'react';
import { useAppTranslation } from 'app/config/i18Config/hooks';
import { Button } from 'shared/ui/Button';
import { Modal } from 'shared/ui/Modal';
import { WarningPing } from 'shared/ui/WarningPing';
import { showNotification } from 'app/providers/NotificationsProvider';
import { Select } from 'shared/ui/Select';
import { Form } from 'antd';
import { BoxDeactivationReason, BoxForListing, useActivateBoxMutation, useDeactivateBoxMutation } from 'entities/Box';
import { useGlobalConfirmModal } from 'app/globalConfirmModal';

interface ActivationBoxModalProps {
  box: BoxForListing;
  action: ReactElement;
}

export const ActivationBoxModal: FC<ActivationBoxModalProps> = memo((props) => {
  const { box, action } = props;

  const [isOpenedModal, setOpenedModal] = useState(false);

  const [form] = Form.useForm();

  const { t } = useAppTranslation(['boxes', 'common']);

  const [activateBox] = useActivateBoxMutation();
  const [deactivateBox] = useDeactivateBoxMutation();
  const { openModal, closeModal } = useGlobalConfirmModal();

  const openDeactivationModal = useCallback((): void => {
    setOpenedModal(true);
  }, []);

  const closeDeactivationModal = useCallback((): void => {
    setOpenedModal(false);
  }, []);

  const deactivationReasonOptions = Object.values(BoxDeactivationReason).map((option) => ({
    label: t(option),
    value: option,
  }));

  const activateBoxHandler = useCallback(
    async (shouldCreateWaitListNote: boolean): Promise<void> => {
      try {
        await activateBox({ boxId: box.boxId, shouldCreateWaitListNote }).unwrap();
        showNotification('info', t('Success', { ns: 'common' }), t('Box has been successfully activated', { ns: 'common' }));
      } catch (error: CustomAny) {
        showNotification('error', t('Error', { ns: 'common' }), t('Error when changing box status', { ns: 'common' }));
      }
    },
    [activateBox, box.boxId, t],
  );

  const openActivationConfirmModal = useCallback((): void => {
    openModal({
      title: t('Box activation'),
      description: t('The size of this box may be in the waiting list. Create a note?'),
      onOk: async () => {
        closeModal();
        await activateBoxHandler(true);
      },
      onCancel: async () => {
        closeModal();
        await activateBoxHandler(false);
      },
    });
  }, [activateBoxHandler, closeModal, openModal, t]);

  const handleActivationClick = useCallback((): void => {
    openActivationConfirmModal();
  }, [openActivationConfirmModal]);

  const changeBoxActivation = useCallback(
    async ({ deactivationReason }: BoxForListing): Promise<void> => {
      try {
        if (box.isActive && deactivationReason) {
          await deactivateBox({ boxId: box.boxId, deactivationReason }).unwrap();
          showNotification('info', t('Success', { ns: 'common' }), t('Box has been successfully deactivated', { ns: 'common' }));
        }
      } catch (error: CustomAny) {
        showNotification('error', t('Error', { ns: 'common' }), t('Error when changing box status', { ns: 'common' }));
      } finally {
        closeDeactivationModal();
      }
    },
    [box.isActive, box.boxId, deactivateBox, t, closeDeactivationModal],
  );

  return (
    <>
      {React.cloneElement(action, { onClick: box.isActive ? openDeactivationModal : handleActivationClick })}

      <Modal className="max-w-[400px]" isOpen={isOpenedModal} onClose={closeDeactivationModal}>
        <Form form={form} layout="vertical" onFinish={changeBoxActivation} className="flex flex-col">
          <div className="mb-2">
            <WarningPing type="warning" />
          </div>
          <div className="font-semibold text-xl mb-3">{t('Box deactivation')}</div>

          <div className="text-primaryLight font-normal">
            {t('Are you sure you want to deactivate this box? You must select deactivation reason!')}
          </div>

          <div className="my-8">
            <Form.Item
              label={t('Deactivation reason')}
              name="deactivationReason"
              rules={[{ required: true, message: `${t('Please, select deactivation reason')}!` }]}
            >
              <Select options={deactivationReasonOptions} placeholder={t('Select deactivation reason')} bordered />
            </Form.Item>
          </div>

          <div className="flex justify-end gap-3">
            <Button theme="secondary" onClick={closeDeactivationModal}>
              {t('Cancel', { ns: 'common' })}
            </Button>

            <Button type="submit">{t('Save', { ns: 'common' })}</Button>
          </div>
        </Form>
      </Modal>
    </>
  );
});
